import { useState } from 'react';
import type { Account } from '@/api';
import { useAccountContext } from '@/app/accounts';
import { needsPassword, ReenterPasswordLink } from '@/components/AccountSwitcher';
import { accountIssuer, ISSUER_NAMES, signsInWithOAuth } from '@/features/oauth-connect/oauth';
import { NoticeRow } from './NoticeRow';

/**
 * The mailbox the can't-sign-in row is about: the active one, when it is a
 * connected mailbox whose own password was rejected. Exported so the
 * background-password row beside it can leave that mailbox to it.
 */
export function reauthBannerAccount(activeAccount: Account | undefined): Account | undefined {
  return activeAccount !== undefined && !activeAccount.isPrimary && needsPassword(activeAccount)
    ? activeAccount
    : undefined;
}

/**
 * "OLS Mail can't sign in to <mailbox>", across the top of the app (#192,
 * plan 06 §6 step 2).
 *
 * A connected mailbox whose saved password the mail host rejected is flagged
 * `reauth-required` and never tried again, so until someone types the
 * password its mail, folders and sends simply stop. This is the middle of the
 * plan's three escalating surfaces: the amber dot is always there in the
 * switcher and the sidebar; this row is there while that mailbox is the one
 * being looked at, since every view is account-scoped and it would otherwise
 * just look empty; the modal comes only when the user acts on it.
 *
 * Only the active mailbox, as the plan says, and for a measured reason too: a
 * row in the flow that appears once the account list arrives pushes a phone's
 * whole page down under the reader (0.19 cumulative layout shift on /mail with
 * a flagged mailbox in the background, against a 0.05 budget). A mailbox
 * nobody is looking at keeps its dot, its sidebar line, and the merged list's
 * "needs its password again" row.
 *
 * The state is the account list, which the live stream's `account.status`
 * frames patch (realtime/apply.ts), so the row comes and goes without a
 * reload. "Re-enter password" opens the modal over the app
 * (reauthPrompt.tsx). The primary mailbox is not this flow; its failure is
 * session expiry (§2).
 *
 * Dismissing hides it for this page load only, like the background-password
 * row beside it; a mailbox that recovers and is flagged again shows again.
 */
export function ReauthBanner() {
  const { activeAccount } = useAccountContext();
  const account = reauthBannerAccount(activeAccount);
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => new Set());

  // A mailbox that is fine again forgets its dismissal, so the next time it is
  // flagged is news (React's "adjust state when props change" pattern).
  const fine = activeAccount !== undefined && !needsPassword(activeAccount) && dismissed.has(activeAccount.id);
  if (fine) {
    setDismissed((previous) => {
      const next = new Set(previous);
      next.delete(activeAccount.id);
      return next;
    });
  }

  if (account === undefined || dismissed.has(account.id)) return null;
  const issuer = accountIssuer(account);

  return (
    <div className="credential-banners">
      <NoticeRow
        label={`OLS Mail can’t sign in to ${account.email}`}
        dismissLabel={`Dismiss the notice for ${account.email}`}
        onDismiss={() => setDismissed((previous) => new Set(previous).add(account.id))}
        action={<ReenterPasswordLink account={account} className="btn btn--ghost" />}
      >
        {signsInWithOAuth(account) ? (
          <>
            OLS Mail can’t sign in to <strong>{account.email}</strong>. Its sign-in with{' '}
            {issuer ? ISSUER_NAMES[issuer] : 'its provider'} ran out or was withdrawn, so its mail isn’t
            updating until you sign in again.
          </>
        ) : (
          <>
            OLS Mail can’t sign in to <strong>{account.email}</strong>. Its saved password stopped
            working, so its mail isn’t updating, and it won’t be tried again until you enter it.
          </>
        )}
      </NoticeRow>
    </div>
  );
}
